import React, { useCallback, useEffect, useMemo, useState } from "react";
import NewPatientAppointment from "./NewPatientAppointment";
import OldPatientAppointment from "./OldPatientAppointment";
import baseUrl from "../../../../api/baseUrl";
import { setPurpose } from "../../../../redux/appointment/appointmentAction";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import socket from "../../../../api/socket";
import { toast } from "react-toastify";
import { showSmsResultToast, notifySmsBalanceChanged } from "../../../../utils/sms/smsToast";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PatientViewModal from "../../../patient/components/PatientViewModal";
import {
  UI,
  AppointmentModalOverlay,
  PatientTypeSegment,
  PurposeMultiSelect,
  DurationChips,
  durationChipOptions,
  formatTimeHm,
  formatSlotDate,
  timeToMinutesHm,
  addMinutesToHm,
  toBackendTime,
} from "../components/appointmentScheduleUi";
import {
  buildNewAppointmentFormState,
  collectUsedSessionQueues,
  formatAppointmentDateYmd,
  NEW_APPOINTMENT_TIME_INPUT_STEP_SECONDS,
  type AppointmentTimeGapMinutes,
  type NewAppointmentFormState,
  parseQueueNumber,
} from "./newAppointmentForm";

const FORM_ID = "new-appointment-fast-form";
const MIN_DURATION_MINUTES = 5;

function NewAppointment({
  doctor,
  setNewAppointmentModal,
  newappointmentDetail,
  onsave,
  timeGapMinutes,
}: {
  doctor: any;
  setNewAppointmentModal: (open: boolean) => void;
  newappointmentDetail: Record<string, unknown>;
  onsave: () => void;
  timeGapMinutes: AppointmentTimeGapMinutes;
}) {
  const [error, seterror] = useState(false);
  const [purposeError, setPurposeError] = useState(false);

  const [selectedOption, setSelectedOption] = useState<"old" | "new">("old");
  const dispatch = useDispatch();
  const purposes = useSelector((state: RootState) => state.appointment);

  const [formData, setformData] = useState<NewAppointmentFormState>(() =>
    buildNewAppointmentFormState(newappointmentDetail)
  );

  const durationOptions = useMemo(
    () => durationChipOptions(formData.interval),
    [formData.interval]
  );
  const timeInputStepSeconds = NEW_APPOINTMENT_TIME_INPUT_STEP_SECONDS;
  const [usedSessionQueues, setUsedSessionQueues] = useState<Set<number>>(
    () => new Set()
  );
  const [queuesLoading, setQueuesLoading] = useState(false);
  const [patientModalOpen, setPatientModalOpen] = useState(false);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientData, setPatientData] = useState<any>(null);
  const [summaryTheme, setSummaryTheme] = useState<"light" | "dark">("light");

  const hasSession = String(newappointmentDetail?.sessionName ?? "").trim() !== "";
  const sessionNameKey = String(newappointmentDetail?.sessionName ?? "").trim();
  const roomNumber = String(newappointmentDetail?.roomNumber ?? "").trim();

  const doctorLabel = useMemo(() => {
    const d = doctor?.find(
      (item: any) => String(item?._id) === String(formData.doctorId)
    );
    if (!d) return "";
    return `Dr. ${[d.title, d.lastName].filter(Boolean).join(" ").trim() || d.lastName || ""}`;
  }, [doctor, formData.doctorId]);

  const getPurpose = useCallback(() => {
    baseUrl
      .get("client/appointment/makeappointment/findAllPurpose")
      .then((response) => {
        dispatch(setPurpose(response.data.data, false));
      })
      .catch((err) => console.log(err));
  }, [dispatch]);

  useEffect(() => {
    if (purposes?.purposeIsEMpty) getPurpose();
  }, [getPurpose, purposes?.purposeIsEMpty]);

  useEffect(() => {
    if (!hasSession || !sessionNameKey || !formData.date || !formData.doctorId) {
      setUsedSessionQueues(new Set());
      return;
    }

    let cancelled = false;
    setQueuesLoading(true);

    const applyQueues = (appointments: any[]) => {
      if (cancelled) return;
      const used = collectUsedSessionQueues(appointments, {
        date: formData.date,
        doctorId: String(formData.doctorId),
        sessionName: sessionNameKey,
      });
      setUsedSessionQueues(used);
      setQueuesLoading(false);
    };

    const todayYmd = formatAppointmentDateYmd(new Date());
    if (formData.date === todayYmd) {
      baseUrl
        .get("client/appointment/makeappointment/findTodayAppoinment")
        .then((res) => applyQueues(res.data?.data ?? []))
        .catch(() => {
          if (!cancelled) {
            setUsedSessionQueues(new Set());
            setQueuesLoading(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    baseUrl
      .get("/appointment/findAllAppointmentsByDoctor", {
        params: { startDate: formData.date, endDate: formData.date },
        headers: { doctorid: formData.doctorId },
      })
      .then(async (res) => {
        const elements = res.data?.data ?? [];
        const ids = elements
          .map((el: any) => el?.data?.appointment?.id ?? el?._id)
          .filter(Boolean);
        if (ids.length === 0) {
          applyQueues([]);
          return;
        }
        const details = await Promise.all(
          ids.map((id: string) =>
            baseUrl
              .get("appointment/findAppointmentById", {
                headers: { appointmentid: id },
              })
              .then((r) => r.data?.data)
              .catch(() => null)
          )
        );
        applyQueues(details.filter(Boolean));
      })
      .catch(() => {
        if (!cancelled) {
          setUsedSessionQueues(new Set());
          setQueuesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hasSession, sessionNameKey, formData.date, formData.doctorId]);

  const queueDuplicate = useMemo(() => {
    if (!hasSession) return false;
    const n = parseQueueNumber(String(formData.queue));
    if (n === null || n === 0) return false;
    return usedSessionQueues.has(n);
  }, [formData.queue, hasSession, usedSessionQueues]);

  const usedQueuesList = useMemo(
    () => Array.from(usedSessionQueues).sort((a, b) => a - b),
    [usedSessionQueues]
  );

  const generateNextQueue = useCallback(() => {
    const used = usedSessionQueues ?? new Set<number>();
    // Queue defaults to 0; "Generate" should pick the next usable number (start at 1).
    let q = 1;
    while (used.has(q)) q++;
    setformData((prev) => ({ ...prev, queue: String(q) }));
  }, [usedSessionQueues]);

  const pickDuration = useCallback((mins: number) => {
    setformData((prev) => {
      const start = formatTimeHm(prev.startTime);
      return {
        ...prev,
        startTime: start,
        interval: String(mins),
        endTime: addMinutesToHm(start, mins),
      };
    });
  }, []);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    if (name === "queue") {
      const digits = String(value).replace(/\D/g, "");
      setformData((prev) => ({ ...prev, queue: digits }));
      return;
    }
    if (name === "startTime") {
      const startHm = formatTimeHm(value);
      const mins = Math.max(
        MIN_DURATION_MINUTES,
        parseInt(String(formData.interval), 10) || timeGapMinutes || MIN_DURATION_MINUTES
      );
      setformData((prev) => ({
        ...prev,
        startTime: startHm,
        endTime: addMinutesToHm(startHm, mins),
        interval: String(mins),
      }));
      return;
    }
    if (name === "endTime") {
      const endHm = formatTimeHm(value);
      const startM = timeToMinutesHm(formData.startTime);
      const endM = timeToMinutesHm(endHm);
      if (!Number.isFinite(startM) || !Number.isFinite(endM) || endM <= startM) {
        toast.warning("End time must be after start time");
        const mins = Math.max(
          MIN_DURATION_MINUTES,
          parseInt(String(formData.interval), 10) || timeGapMinutes || MIN_DURATION_MINUTES
        );
        setformData((prev) => ({
          ...prev,
          endTime: addMinutesToHm(prev.startTime, mins),
          interval: String(mins),
        }));
        return;
      }
      setformData((prev) => ({
        ...prev,
        endTime: endHm,
        interval: String(endM - startM),
      }));
      return;
    }
    if (type === "checkbox") {
      setformData((prev: any) => ({ ...prev, [name]: checked }));
      return;
    }
    setformData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleCancel = useCallback(() => {
    setNewAppointmentModal(false);
  }, [setNewAppointmentModal]);

  const handleViewPatient = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const pid = formData.patientId;
      if (!pid) return;
      setPatientModalOpen(true);
      setPatientLoading(true);
      try {
        const res = await baseUrl.get("patient/findById", {
          headers: { patientid: pid },
        });
        setPatientData(res.data?.data ?? null);
      } catch (err) {
        console.error(err);
        setPatientData(null);
      } finally {
        setPatientLoading(false);
      }
    },
    [formData.patientId]
  );

  const closePatientModal = useCallback(() => {
    setPatientModalOpen(false);
    setPatientData(null);
  }, []);

  useEffect(() => {
    const htmlTheme = document.documentElement.getAttribute("data-theme") as
      | "light"
      | "dark";
    const savedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || htmlTheme;
    if (savedTheme) setSummaryTheme(savedTheme);
  }, []);

  const handleSubmit = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.preventDefault();

      if (!formData.purpose?.length) {
        setPurposeError(true);
        return;
      }
      setPurposeError(false);

      const startM = timeToMinutesHm(formData.startTime);
      const endM = timeToMinutesHm(formData.endTime);
      if (!Number.isFinite(startM) || !Number.isFinite(endM) || endM <= startM) {
        toast.warning("End time must be after start time");
        return;
      }

      const durationMinutes = Math.max(MIN_DURATION_MINUTES, endM - startM);

      if (hasSession) {
        const queueNum = parseQueueNumber(String(formData.queue));
        if (queueNum === null) {
          toast.warning("Queue must be a non-negative number");
          return;
        }
        if (queueNum !== 0 && usedSessionQueues.has(queueNum)) {
          toast.error(
            `Queue #${queueNum} is already used in session "${sessionNameKey}"`
          );
          return;
        }
      }

      const submitPayload: Record<string, any> = {
        ...formData,
        startTime: toBackendTime(formData.startTime),
        endTime: toBackendTime(addMinutesToHm(formData.startTime, durationMinutes)),
        interval: String(durationMinutes),
      };
      if (hasSession) {
        submitPayload.queue = parseQueueNumber(String(formData.queue)) ?? 0;
        submitPayload.sessionName = String(newappointmentDetail?.sessionName ?? "").trim();
      } else {
        delete submitPayload.queue;
        delete submitPayload.sessionName;
      }
      if (roomNumber) submitPayload.roomNumber = roomNumber;
      else delete submitPayload.roomNumber;
      if (formData.walkIn) submitPayload.status = "walkin";
      else if (formData.callIn) submitPayload.status = "callin";
      else delete submitPayload.status;
      delete submitPayload.walkIn;
      delete submitPayload.callIn;

      const purposeMatches = (formData.purpose ?? [])
        .map((id) => purposes?.purpose?.find((item: any) => item._id === id))
        .filter(Boolean);
      const purposeNames = purposeMatches
        .map((item: any) => item.purposeName)
        .filter(Boolean)
        .join(", ");

      socket.emit("newAppointment", {
        doctorId: formData.doctorId,
        patientId: { _id: formData?.patientId, fullName: formData?.fullName },
        activeTime: formData?.activeTime,
        date: formData.date,
        startTime: submitPayload.startTime,
        endTime: submitPayload.endTime,
        interval: submitPayload.interval,
        purpose: formData.purpose,
        purposeId: { purposeName: purposeNames },
        ...(hasSession
          ? { queue: submitPayload.queue, sessionName: submitPayload.sessionName }
          : {}),
        ...(roomNumber ? { roomNumber } : {}),
        ...(formData.walkIn
          ? { status: "walkin" }
          : formData.callIn
            ? { status: "callin" }
            : {}),
      });

      seterror(false);

      if (selectedOption === "old") {
        if (!formData.patientId) {
          seterror(true);
          return;
        }
        baseUrl
          .post("/client/appointment/makeappointment/makeAppointmentByPatient", submitPayload)
          .then((response) => {
            showSmsResultToast(response.data?.sms);
            notifySmsBalanceChanged();
            onsave();
            setNewAppointmentModal(false);
          })
          .catch((err) => console.log(err));
      } else {
        baseUrl
          .post("/client/appointment/makeappointment/makeFreshAppointment", submitPayload)
          .then((response) => {
            showSmsResultToast(response.data?.sms);
            notifySmsBalanceChanged();
            onsave();
            setNewAppointmentModal(false);
          })
          .catch((err) => console.log(err));
      }
    },
    [
      formData,
      hasSession,
      newappointmentDetail?.sessionName,
      onsave,
      purposes?.purpose,
      roomNumber,
      selectedOption,
      sessionNameKey,
      setNewAppointmentModal,
      usedSessionQueues,
    ]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (patientModalOpen) {
          closePatientModal();
          return;
        }
        handleCancel();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (!patientModalOpen) handleSubmit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleCancel, handleSubmit, patientModalOpen, closePatientModal]);

  const slotLine = [
    formData.walkIn ? "Walk-in" : formData.callIn ? "Call-in" : "",
    formatSlotDate(formData.date),
    `${formatTimeHm(formData.startTime)}–${formatTimeHm(formData.endTime)}`,
    doctorLabel,
    hasSession ? String(newappointmentDetail.sessionName).trim() : "",
    roomNumber ? `Room ${roomNumber}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <AppointmentModalOverlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-appointment-title"
    >
      <NewAppointmentShell
        slotLine={slotLine}
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        formData={formData}
        setformData={setformData}
        onGenerateQueue={generateNextQueue}
        error={error}
        seterror={seterror}
        purposeError={purposeError}
        setPurposeError={setPurposeError}
        purposes={purposes}
        hasSession={hasSession}
        newappointmentDetail={newappointmentDetail}
        handleChange={handleChange}
        pickDuration={pickDuration}
        queueDuplicate={queueDuplicate}
        usedQueuesList={usedQueuesList}
        queuesLoading={queuesLoading}
        durationOptions={durationOptions}
        timeInputStepSeconds={timeInputStepSeconds}
        onViewPatient={
          selectedOption === "old" && formData.patientId ? handleViewPatient : undefined
        }
      />
      <PatientViewModal
        isOpen={patientModalOpen}
        onClose={closePatientModal}
        patientData={patientData}
        loading={patientLoading}
        theme={summaryTheme}
        overlayClassName="fixed inset-0 z-[210] flex h-dvh w-full flex-col overflow-y-auto overscroll-contain"
      />
    </AppointmentModalOverlay>
  );
}

type NewAppointmentShellProps = {
  slotLine: string;
  handleCancel: () => void;
  handleSubmit: (e?: React.SyntheticEvent) => void;
  selectedOption: "old" | "new";
  setSelectedOption: React.Dispatch<React.SetStateAction<"old" | "new">>;
  formData: NewAppointmentFormState;
  setformData: React.Dispatch<React.SetStateAction<NewAppointmentFormState>>;
  onGenerateQueue: () => void;
  error: boolean;
  seterror: React.Dispatch<React.SetStateAction<boolean>>;
  purposeError: boolean;
  setPurposeError: React.Dispatch<React.SetStateAction<boolean>>;
  purposes: RootState["appointment"];
  hasSession: boolean;
  newappointmentDetail: Record<string, unknown>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  pickDuration: (mins: number) => void;
  queueDuplicate: boolean;
  usedQueuesList: number[];
  queuesLoading: boolean;
  durationOptions: number[];
  timeInputStepSeconds: number;
  onViewPatient?: (e: React.MouseEvent) => void;
};

function NewAppointmentShell(props: NewAppointmentShellProps) {
  const {
    slotLine,
    handleCancel,
    handleSubmit,
    selectedOption,
    setSelectedOption,
    formData,
    setformData,
    onGenerateQueue,
    error,
    seterror,
    purposeError,
    setPurposeError,
    purposes,
    hasSession,
    newappointmentDetail,
    handleChange,
    pickDuration,
    queueDuplicate,
    usedQueuesList,
    queuesLoading,
    durationOptions,
    timeInputStepSeconds,
    onViewPatient,
  } = props;

  return (
    <div className={`${UI.modalShell} max-w-lg`}>
      <header className={`${UI.modalHeader} py-3`}>
        <div className="min-w-0 flex-1">
          <h2
            id="new-appointment-title"
            className="text-base font-semibold text-slate-900 tracking-tight"
          >
            New appointment
          </h2>
          <p className="mt-1 flex items-start gap-1 text-xs text-slate-600 leading-snug">
            <AccessTimeIcon sx={{ fontSize: 14 }} className="shrink-0 mt-0.5 text-primary" />
            <span className="truncate">{slotLine}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={handleCancel}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
          title="Close (Esc)"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </header>

      <div className={`${UI.modalBody} py-3 space-y-3`}>
        <PatientTypeSegment
          value={selectedOption}
          onChange={(v) => {
            setSelectedOption(v);
            if (v === "old") seterror(false);
          }}
        />

        <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-3">
          <div className="rounded-xl border border-slate-200/60 bg-white p-3 space-y-2">
            <label className={`${UI.label} mb-0`}>Patient</label>
            {selectedOption === "old" ? (
              <OldPatientAppointment
                setformData={setformData}
                formData={formData}
                error={error}
                setError={seterror}
                autoFocus
                onViewPatient={onViewPatient}
              />
            ) : (
              <NewPatientAppointment setformData={setformData} formData={formData} />
            )}
          </div>

          <div className="rounded-xl border border-slate-200/60 bg-white p-3 space-y-2">
            <label className={`${UI.label} mb-0`}>
              Purpose <span className="text-red-500">*</span>
            </label>
            <PurposeMultiSelect
              purposes={purposes?.purpose}
              value={formData.purpose}
              onChange={(ids) => {
                setPurposeError(false);
                setformData((prev) => ({ ...prev, purpose: ids }));
              }}
              error={purposeError}
            />
            {purposeError ? (
              <p className="text-xs text-red-500">Select at least one purpose</p>
            ) : null}
          </div>

          <div className="rounded-xl border border-slate-200/60 bg-white p-3 space-y-2">
            {formData.callIn ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 shrink-0">Start</span>
                  <input
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    step={timeInputStepSeconds}
                    onChange={handleChange}
                    className={`flex-1 ${UI.input} py-2`}
                  />
                </div>
                <label className={`${UI.label} mb-0`}>Duration</label>
                <DurationChips
                  value={formData.interval}
                  options={durationOptions}
                  onChange={pickDuration}
                />
                <p className="pt-1 text-xs text-slate-500">
                  End time will be calculated automatically as{" "}
                  <span className="font-semibold text-slate-700">
                    {formatTimeHm(formData.endTime) || "—"}
                  </span>
                  .
                </p>
              </>
            ) : (
              <>
                <label className={`${UI.label} mb-0`}>Duration</label>
                <DurationChips
                  value={formData.interval}
                  options={durationOptions}
                  onChange={pickDuration}
                />
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs text-slate-500 shrink-0">End</span>
                  <input
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    min={formData.startTime || undefined}
                    step={timeInputStepSeconds}
                    onChange={handleChange}
                    className={`flex-1 ${UI.input} py-2`}
                  />
                </div>
              </>
            )}
          </div>

          {hasSession ? (
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200/60 bg-white p-3">
              <div>
                <span className={UI.label}>Session</span>
                <p className="text-sm font-medium text-slate-800 mt-0.5">
                  {String(newappointmentDetail.sessionName).trim()}
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <label htmlFor="new-appointment-queue" className={UI.label}>
                    Queue
                  </label>
                  <button
                    type="button"
                    onClick={onGenerateQueue}
                    disabled={queuesLoading}
                    className={`${UI.btnGhost} text-xs py-1 disabled:opacity-60 disabled:cursor-not-allowed`}
                    title="Generate next available queue number"
                  >
                    Generate
                  </button>
                </div>
                <input
                  id="new-appointment-queue"
                  type="text"
                  inputMode="numeric"
                  name="queue"
                  autoComplete="off"
                  placeholder="0"
                  value={formData.queue}
                  onChange={handleChange}
                  onBlur={(e) => {
                    if (String(e.target.value).trim() === "") {
                      setformData((prev) => ({ ...prev, queue: "0" }));
                    }
                  }}
                  aria-invalid={queueDuplicate}
                  className={`${UI.input} py-2 ${queueDuplicate
                      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                      : ""
                    }`}
                />
                {queueDuplicate ? (
                  <p className="mt-1 text-xs text-red-500">
                    This queue number is already used in this session
                  </p>
                ) : null}
                {!queueDuplicate && usedQueuesList.length > 0 ? (
                  <p className="mt-1 text-xs text-slate-500">
                    {queuesLoading
                      ? "Loading used queues…"
                      : `Used in session: ${usedQueuesList.join(", ")}`}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </form>
      </div>

      <footer className={`${UI.modalFooter} py-3`}>
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={handleCancel}
            className={`${UI.btnSecondary} px-4 py-2`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form={FORM_ID}
            className={`${UI.btnPrimary} flex-1 py-2.5`}
          >
            <SaveIcon sx={{ fontSize: 18 }} />
            Book appointment
          </button>
        </div>
        <p className="text-[10px] text-center text-slate-400 mt-2">
          Esc to close · Ctrl+Enter to save · Enter picks first patient
        </p>
      </footer>
    </div>
  );
}

export default NewAppointment;
